// src/pages/Signup.tsx
import { FormEvent, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthProvider";
import BankSelect from "../components/BankSelect";

const API_BASE = (import.meta.env.VITE_API_BASE || "").replace(/\/$/, "");
const ONE_WON_DRAFT_KEY = "signup_onewon_draft_v1";
const EMAIL_DOMAINS = ["naver.com", "gmail.com", "nate.com", "daum.net", "kakao.com", "hotmail.com"];

type WizardStep = 0 | 1 | 2 | 3;
type VerificationStage = "idle" | "idcardDone";

function normalizePhone(v: string) {
  return v.replace(/[-\s]/g, "");
}

function isValidPhone(v: string) {
  const p = normalizePhone(v);
  return /^\+?\d{9,15}$/.test(p);
}

function isValidEmail(v: string) {
  const e = v.trim();
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
}

function isMobileUA() {
  if (typeof navigator === "undefined") return false;
  return /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
}

function formatBirthInput(v: string) {
  const s = v.replace(/[^\d]/g, "").slice(0, 8);
  const y = s.slice(0, 4);
  const m = s.slice(4, 6);
  const d = s.slice(6, 8);
  if (s.length <= 4) return y;
  if (s.length <= 6) return `${y}-${m}`;
  return `${y}-${m}-${d}`;
}

function isValidBirth(v: string) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(v)) return false;
  const [yy, mm, dd] = v.split("-").map(Number);
  const dt = new Date(yy, mm - 1, dd);
  if (dt.getFullYear() !== yy || dt.getMonth() !== mm - 1 || dt.getDate() !== dd) return false;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  dt.setHours(0, 0, 0, 0);
  return dt <= today;
}

export default function Signup() {
  const nav = useNavigate();
  const { login } = useAuth();
  const isMobile = isMobileUA();

  const [step, setStep] = useState<WizardStep>(0);
  const [introOpen, setIntroOpen] = useState(true);
  const [policyModalOpen, setPolicyModalOpen] = useState(false);
  const [policyTab, setPolicyTab] = useState<"terms" | "privacy">("terms");

  const [name, setName] = useState("");
  const [birth, setBirth] = useState("");
  const [gender, setGender] = useState<"male" | "female" | "other" | "">("");
  const [phone, setPhone] = useState("");
  const [emailId, setEmailId] = useState("");
  const [emailDomainPreset, setEmailDomainPreset] = useState("naver.com");
  const [emailDomainCustom, setEmailDomainCustom] = useState("");
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [agree, setAgree] = useState(false);

  const [emailCheckBusy, setEmailCheckBusy] = useState(false);
  const [emailAvailable, setEmailAvailable] = useState<boolean | null>(null);
  const [emailCheckMessage, setEmailCheckMessage] = useState<string | null>(null);
  const [lastCheckedEmail, setLastCheckedEmail] = useState("");

  const [verificationStage, setVerificationStage] = useState<VerificationStage>("idle");
  const [userId, setUserId] = useState<number | null>(null);
  const [bankCode, setBankCode] = useState("");
  const [accountNo, setAccountNo] = useState("");
  const [requestId, setRequestId] = useState<string | null>(null);
  const [started, setStarted] = useState(false);
  const [authCode, setAuthCode] = useState("");

  const [busy, setBusy] = useState(false);
  const [selectedFileName, setSelectedFileName] = useState("");
  const idFileRef = useRef<HTMLInputElement | null>(null);

  const emailDomain = emailDomainPreset === "direct" ? emailDomainCustom.trim().toLowerCase() : emailDomainPreset;
  const email = emailId.trim() && emailDomain ? `${emailId.trim()}@${emailDomain}` : "";
  const emailIsValid = isValidEmail(email);
  const progress = ((step + 1) / 4) * 100;
  const stepTitle =
    step === 0 ? "실명 확인" : step === 1 ? "생년월일" : step === 2 ? "가입 정보" : verificationStage === "idle" ? "신분증 인증" : "1원 인증";

  const inputClass =
    "w-full rounded-2xl border border-orange-200/80 bg-white/90 px-5 py-3.5 text-center text-[18px] font-bold text-slate-900 outline-none transition duration-300 placeholder:text-slate-300 focus:-translate-y-0.5 focus:border-orange-400 focus:ring-4 focus:ring-orange-100";
  const labelClass = "block text-center text-[13px] font-bold text-slate-700";
  const primaryButton =
    "w-full rounded-2xl bg-gradient-to-r from-[#ff8a1f] to-[#f97316] px-6 py-4 text-[16px] font-extrabold text-white shadow-[0_18px_38px_rgba(249,115,22,.30)] transition duration-300 hover:-translate-y-0.5 hover:brightness-105 active:translate-y-px disabled:cursor-not-allowed disabled:opacity-45";

  useEffect(() => {
    const em = email.trim().toLowerCase();
    if (emailAvailable !== null && em !== lastCheckedEmail) {
      setEmailAvailable(null);
      setEmailCheckMessage(null);
    }
  }, [email, emailAvailable, lastCheckedEmail]);

  useEffect(() => {
    if (step !== 2) return;
    const em = email.trim().toLowerCase();
    if (!em || !isValidEmail(em) || em === lastCheckedEmail) return;

    const timer = window.setTimeout(() => {
      void onCheckEmail(em, true);
    }, 450);
    return () => window.clearTimeout(timer);
  }, [step, email, lastCheckedEmail]);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(ONE_WON_DRAFT_KEY);
      if (!raw) return;
      const draft = JSON.parse(raw);
      if (!draft || typeof draft !== "object") return;
      if (Date.now() - Number(draft.savedAt || 0) > 1000 * 60 * 60 * 6) {
        localStorage.removeItem(ONE_WON_DRAFT_KEY);
        return;
      }

      if (draft.name) setName(String(draft.name));
      if (draft.userId) setUserId(Number(draft.userId));
      if (draft.bankCode) setBankCode(String(draft.bankCode));
      if (draft.accountNo) setAccountNo(String(draft.accountNo));
      if (draft.authCode) setAuthCode(String(draft.authCode));
      if (draft.requestId) setRequestId(String(draft.requestId));
      if (typeof draft.started === "boolean") setStarted(Boolean(draft.started));
      if (draft.verificationStage === "idcardDone") setVerificationStage("idcardDone");
      if (draft.verificationStage === "idcardDone" && draft.userId) setStep(3);
    } catch {
      localStorage.removeItem(ONE_WON_DRAFT_KEY);
    }
  }, []);

  useEffect(() => {
    const hasDraftState = verificationStage === "idcardDone" || !!bankCode || !!accountNo || !!authCode;
    if (!hasDraftState) return;

    localStorage.setItem(
      ONE_WON_DRAFT_KEY,
      JSON.stringify({
        savedAt: Date.now(),
        step,
        name: name.trim(),
        userId,
        verificationStage,
        bankCode,
        accountNo,
        authCode,
        requestId,
        started,
      })
    );
  }, [step, name, userId, verificationStage, bankCode, accountNo, authCode, requestId, started]);

  async function onCheckEmail(emailParam: string, silent = false) {
    const em = emailParam.trim().toLowerCase();
    if (!em || !isValidEmail(em)) {
      if (!silent) alert("이메일 형식을 확인해 주세요.");
      return;
    }

    setEmailCheckBusy(true);
    setEmailCheckMessage("중복 확인 중...");
    setEmailAvailable(null);

    try {
      const resp = await fetch(`${API_BASE}/api/signup/check-email?email=${encodeURIComponent(em)}`);
      const j = await resp.json().catch(() => null);
      if (!resp.ok || !j) throw new Error(j?.message || `중복 확인 실패 (${resp.status})`);

      setEmailAvailable(Boolean(j.available));
      setEmailCheckMessage(j.available ? "사용 가능한 이메일입니다." : "이미 사용 중인 이메일입니다.");
      setLastCheckedEmail(em);
    } catch (err: any) {
      console.error("check-email 오류:", err);
      setEmailAvailable(null);
      setEmailCheckMessage(err?.message || "이메일 중복 확인 중 오류가 발생했습니다.");
    } finally {
      setEmailCheckBusy(false);
    }
  }

  function goPrevStep() {
    if (step === 0) {
      nav(-1);
      return;
    }
    setStep((prev) => Math.max(0, prev - 1) as WizardStep);
  }

  function goNextStep() {
    if (step === 0) {
      if (!name.trim()) return alert("이름을 입력해 주세요.");
      setStep(1);
      return;
    }

    if (step === 1) {
      if (!birth || !isValidBirth(birth)) return alert("생년월일을 정확히 입력해 주세요. (예: 1990-01-31)");
      if (!gender) return alert("성별을 선택해 주세요.");
      setStep(2);
      return;
    }

    if (step === 2) {
      if (!agree) return alert("회원가입 정책에 동의해 주세요.");
      if (!isValidPhone(phone)) return alert("연락처 형식이 올바르지 않습니다. 예) 01012345678 또는 +821012345678");
      if (!emailIsValid) return alert("이메일 형식을 확인해 주세요.");
      if (emailCheckBusy) return alert("이메일 중복 확인 중입니다. 잠시만 기다려 주세요.");
      if (emailAvailable !== true) return alert("이메일 중복 확인이 완료되지 않았습니다.");
      if (password.length < 6) return alert("비밀번호는 6자 이상이어야 합니다.");
      if (password !== password2) return alert("비밀번호가 일치하지 않습니다.");
      setStep(3);
    }
  }

  async function onSubmitIdCard(e: FormEvent) {
    e.preventDefault();
    if (!birth || !isValidBirth(birth) || !gender) {
      alert("생년월일과 성별을 먼저 입력해 주세요.");
      setStep(1);
      return;
    }

    const file = idFileRef.current?.files?.[0];
    if (!file) {
      idFileRef.current?.click();
      return;
    }
    if (!/^image\/(jpe?g|png|webp)$/i.test(file.type || "")) return alert("이미지(JPG/PNG/WEBP)만 업로드 가능합니다.");
    if (file.size > 20 * 1024 * 1024) return alert("파일 용량은 최대 20MB입니다.");

    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("name", name.trim());
      fd.append("email", email.trim().toLowerCase());
      fd.append("password", password);
      fd.append("phone", normalizePhone(phone));
      fd.append("idcard", file);
      fd.append("birth", birth);
      fd.append("gender", gender);

      const resp = await fetch(`${API_BASE}/api/signup`, { method: "POST", body: fd });
      const j = await resp.json().catch(() => null);
      if (!resp.ok || !j?.ok || !j?.user?.id) throw new Error(j?.message || `회원가입(신분증 인증) 실패 (${resp.status})`);

      setUserId(j.user.id);
      setVerificationStage("idcardDone");
      setSelectedFileName(file.name);
      localStorage.setItem("signupEmail", email.trim().toLowerCase());
      localStorage.setItem("signupPassword", password);
      alert("신분증 인증이 완료되었습니다. 이어서 1원 계좌 인증을 진행해 주세요.");
    } catch (err: any) {
      console.error("signup(idcard) 오류:", err);
      alert(err?.message || "신분증 인증 실패");
    } finally {
      setBusy(false);
    }
  }

  async function startOneWonRequest(isResend: boolean) {
    if (!userId) return alert("먼저 신분증 인증을 완료해 주세요.");
    if (!bankCode) return alert("인증은행을 선택해 주세요.");
    if (!accountNo.trim()) return alert("계좌번호를 입력해 주세요.");

    setBusy(true);
    try {
      const resp = await fetch(`${API_BASE}/api/onboarding/start`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId, bankCode, accountNo, holderName: name.trim(), signupName: name.trim() }),
      });
      const j = await resp.json().catch(() => null);
      if (!resp.ok || !j?.ok) {
        const detail = j?.error || j?.message || j?.detail?.message || (j?.code === "NAME_MISMATCH" ? "회원 이름과 예금주명이 일치하지 않습니다." : "");
        throw new Error(detail || `1원 인증 시작 실패 (${resp.status})`);
      }

      setRequestId(j.requestId ?? j?.provider?.requestId ?? null);
      setStarted(true);
      alert(isResend ? "1원이 다시 입금되었습니다. 입금자명에 표시된 인증코드를 확인해 주세요." : "1원이 입금되었습니다. 입금자명에 표시된 인증코드를 입력해 주세요.");
    } catch (err: any) {
      console.error("onboarding/start 오류:", err);
      alert(err?.message || "시작 실패");
    } finally {
      setBusy(false);
    }
  }

  async function onStartOneWon(e: FormEvent) {
    e.preventDefault();
    await startOneWonRequest(false);
  }

  async function onConfirmOneWon(e: FormEvent) {
    e.preventDefault();
    if (!userId) return alert("먼저 신분증 인증을 완료해 주세요.");

    setBusy(true);
    try {
      const resp = await fetch(`${API_BASE}/api/onboarding/confirm`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId, authCode: authCode.trim(), requestId }),
      });
      const j = await resp.json().catch(() => null);
      if (!resp.ok || !j?.ok) {
        const detail = j?.error || j?.message || j?.detail?.message || (j?.code === "NAME_MISMATCH" ? "회원 이름과 예금주명이 일치하지 않습니다." : "");
        throw new Error(detail || `1원 인증 실패 (${resp.status})`);
      }

      const em = localStorage.getItem("signupEmail") || email.trim().toLowerCase();
      const pw = localStorage.getItem("signupPassword") || password;
      await login(String(em), String(pw));
      localStorage.removeItem("signupEmail");
      localStorage.removeItem("signupPassword");
      localStorage.removeItem(ONE_WON_DRAFT_KEY);

      alert("회원가입이 완료되었습니다.");
      nav("/", { replace: true });
    } catch (err: any) {
      console.error("onboarding/confirm 오류:", err);
      alert(err?.message || "인증 실패");
    } finally {
      setBusy(false);
    }
  }

  const renderBody = () => {
    if (step === 0) {
      return (
        <div className="space-y-6 text-center">
          <div>
            <input className={`${inputClass} mt-3 text-[30px] sm:text-[34px]`} value={name} onChange={(e) => setName(e.target.value)} placeholder="이름을 입력하세요" autoFocus />
          </div>
        </div>
      );
    }

    if (step === 1) {
      return (
        <div className="space-y-6 text-center">
          <div>
            <label className={labelClass}>생년월일</label>
            <input
              type={isMobile ? "text" : "date"}
              value={birth}
              onChange={(e) => setBirth(isMobile ? formatBirthInput(e.target.value) : e.target.value)}
              placeholder={isMobile ? "YYYY-MM-DD" : undefined}
              inputMode={isMobile ? "numeric" : undefined}
              className={`${inputClass} mt-3 text-[26px] sm:text-[30px]`}
            />
          </div>
          <div>
            <label className={labelClass}>성별</label>
            <div className="mt-2 grid grid-cols-2 gap-3">
              {[
                { value: "female", label: "여성" },
                { value: "male", label: "남성" },
              ].map((item) => (
                <button
                  key={item.value}
                  type="button"
                  onClick={() => setGender(item.value as typeof gender)}
                  className={`rounded-2xl border px-4 py-3 text-center text-[17px] font-extrabold transition duration-300 hover:-translate-y-0.5 ${gender === item.value ? "border-orange-400 bg-orange-50 text-orange-700 shadow-[0_12px_28px_rgba(249,115,22,.12)]" : "border-slate-200 bg-white text-slate-700"}`}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      );
    }

    if (step === 2) {
      return (
        <div className="space-y-5 text-center">
          <div>
            <label className={labelClass}>연락처</label>
            <input className={`${inputClass} mt-2`} value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="01012345678" inputMode="tel" />
          </div>
          <div>
            <label className={labelClass}>이메일</label>
            <div className="mt-2 grid grid-cols-[1fr_auto_1fr] items-center gap-2">
              <input className={inputClass} value={emailId} onChange={(e) => setEmailId(e.target.value.replace(/\s/g, ""))} placeholder="id" />
              <span className="text-sm font-black text-slate-400">@</span>
              <select className={`${inputClass} px-2 text-[14px]`} value={emailDomainPreset} onChange={(e) => setEmailDomainPreset(e.target.value)}>
                {EMAIL_DOMAINS.map((d) => <option key={d} value={d}>{d}</option>)}
                <option value="direct">직접 입력</option>
              </select>
            </div>
            {emailDomainPreset === "direct" ? <input className={`${inputClass} mt-2`} value={emailDomainCustom} onChange={(e) => setEmailDomainCustom(e.target.value.replace(/\s/g, ""))} placeholder="example.com" /> : null}
            {emailCheckMessage ? <p className={`mt-1 text-xs font-semibold ${emailAvailable ? "text-orange-600" : "text-rose-500"}`}>{emailCheckMessage}</p> : null}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>비밀번호</label>
              <input type="password" className={`${inputClass} mt-2`} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="6자 이상" />
            </div>
            <div>
              <label className={labelClass}>확인</label>
              <input type="password" className={`${inputClass} mt-2`} value={password2} onChange={(e) => setPassword2(e.target.value)} placeholder="재입력" />
            </div>
          </div>
          <label className="flex items-start gap-3 rounded-2xl border border-orange-100 bg-orange-50/70 px-4 py-3 text-[12px] font-medium leading-5 text-slate-700">
            <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="mt-0.5 h-4 w-4 accent-orange-500" />
            <span>
              주류페이 <button type="button" className="font-bold underline decoration-orange-300 underline-offset-2" onClick={() => { setPolicyTab("terms"); setPolicyModalOpen(true); }}>서비스 이용약관</button> 및 <button type="button" className="font-bold underline decoration-orange-300 underline-offset-2" onClick={() => { setPolicyTab("privacy"); setPolicyModalOpen(true); }}>개인정보 처리방침</button>에 동의합니다.
            </span>
          </label>
        </div>
      );
    }

    return verificationStage === "idle" ? (
      <form onSubmit={onSubmitIdCard} className="space-y-5 text-center">
        <div className="rounded-2xl border border-orange-100 bg-orange-50/70 px-4 py-3 text-[13px] font-medium leading-5 text-orange-900">신분증이 반사되지 않고 테두리가 모두 보이게 촬영해 주세요.</div>
        <button type="button" onClick={() => idFileRef.current?.click()} className="w-full rounded-2xl bg-slate-900 px-5 py-4 text-[15px] font-extrabold text-white transition hover:bg-slate-800">
          {selectedFileName ? "파일 다시 선택" : "신분증 파일 선택"}
        </button>
        <input ref={idFileRef} type="file" accept="image/*" className="hidden" onChange={(e) => setSelectedFileName(e.target.files?.[0]?.name || "")} />
        {selectedFileName ? <div className="truncate rounded-2xl bg-emerald-50 px-4 py-3 text-[13px] font-bold text-emerald-700">선택됨: {selectedFileName}</div> : null}
        <button type="submit" disabled={busy || !selectedFileName} className={primaryButton}>{busy ? "인증 중..." : "신분증 인증 시작"}</button>
      </form>
    ) : (
      <div className="space-y-5 text-center">
        <form onSubmit={onStartOneWon} className="space-y-4">
          <BankSelect label="" value={bankCode} onChange={setBankCode} required selectClassName={inputClass} />
          <input className={inputClass} value={accountNo} onChange={(e) => setAccountNo(e.target.value.replace(/[^0-9-]/g, ""))} placeholder="계좌번호 숫자만 입력" inputMode="numeric" />
          <input className={`${inputClass} text-slate-600`} value={name.trim()} readOnly aria-label="예금주명" />
          <div className="grid grid-cols-[1fr_auto] gap-2">
            <button type="submit" disabled={busy} className={primaryButton}>{busy ? "전송 중..." : "1원 인증 시작"}</button>
            <button type="button" onClick={() => startOneWonRequest(true)} disabled={busy || !started} className="rounded-2xl border border-orange-300 bg-white px-4 text-[13px] font-extrabold text-orange-700 disabled:opacity-45">재전송</button>
          </div>
        </form>
        <form onSubmit={onConfirmOneWon} className="space-y-3 border-t border-orange-100 pt-4">
          <input className={inputClass} value={authCode} onChange={(e) => setAuthCode(e.target.value.toUpperCase())} placeholder="인증코드(송금자명)" />
          <button type="submit" disabled={busy || !started} className={primaryButton}>{busy ? "확인 중..." : "회원가입 완료"}</button>
        </form>
      </div>
    );
  };

  return (
    <div className="min-h-[calc(100vh-64px)] overflow-hidden bg-white px-4 py-4 sm:px-6">
      <style>{`@keyframes signupStepIn { from { opacity: 0; transform: translateY(14px) scale(.985); } to { opacity: 1; transform: translateY(0) scale(1); } } @keyframes signupCardIn { from { opacity: 0; transform: translateY(28px) scale(.96); } to { opacity: 1; transform: translateY(0) scale(1); } } @keyframes modalFade { from { opacity: 0; } to { opacity: 1; } } @keyframes modalPop { from { opacity: 0; transform: translateY(18px) scale(.92); } to { opacity: 1; transform: translateY(0) scale(1); } }`}</style>
      <div className="mx-auto flex min-h-[calc(100vh-96px)] max-w-2xl items-start justify-center pt-[10vh] sm:pt-[13vh] lg:pt-[15vh]">
        <div className="w-full overflow-hidden rounded-[34px] border border-white/90 bg-white/92 shadow-[0_30px_90px_rgba(154,74,18,.20)] backdrop-blur animate-[signupCardIn_.42s_cubic-bezier(.22,1,.36,1)]">
          <div className="h-2 bg-orange-100"><div className="h-full rounded-r-full bg-gradient-to-r from-[#ff8a1f] to-[#f97316] transition-all duration-500" style={{ width: `${progress}%` }} /></div>
          <div className="grid grid-cols-[auto_1fr_auto] items-center gap-4 px-6 py-5 sm:px-8">
            <button type="button" onClick={goPrevStep} className="rounded-full border border-orange-200 bg-white px-5 py-2.5 text-[14px] font-extrabold text-orange-700 transition hover:-translate-x-0.5 hover:bg-orange-50">이전</button>
            <div className="text-center">
              <div className="text-[12px] font-black uppercase tracking-[.22em] text-orange-400">JULYU PAY</div>
              <div className="mt-1 text-[22px] font-extrabold tracking-tight text-slate-900 sm:text-[24px]">{stepTitle}</div>
            </div>
            <div className="text-[14px] font-black text-slate-400">{step + 1}/4</div>
          </div>
          <div className="px-6 pb-6 sm:px-8 sm:pb-7 animate-[signupStepIn_.30s_cubic-bezier(.22,1,.36,1)]">{renderBody()}</div>
          {step < 3 ? <div className="border-t border-orange-100 px-6 py-5 sm:px-8"><button type="button" onClick={goNextStep} className={primaryButton}>계속</button></div> : null}
        </div>
      </div>

      {introOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/35 px-4 animate-[modalFade_.20s_ease-out]">
          <div className="w-full max-w-[440px] rounded-[28px] bg-white px-6 py-6 shadow-[0_28px_80px_rgba(15,23,42,.24)] animate-[modalPop_.30s_cubic-bezier(.22,1,.36,1)] sm:px-7">
            <div className="inline-flex rounded-full bg-blue-100 px-3.5 py-1.5 text-[13px] font-extrabold text-blue-700">가입 안내</div>
            <h2 className="mt-5 text-[18px] font-extrabold tracking-tight text-slate-900 sm:text-[20px]">신분증과 본인 명의 계좌를 미리 준비하세요.</h2>
            <p className="mt-2 text-[13px] font-medium leading-6 text-slate-500">회원가입은 신분증 인증·1원 계좌인증을 통해 진행됩니다.</p>
            <div className="mt-7 flex justify-end">
              <button type="button" onClick={() => setIntroOpen(false)} className="rounded-2xl bg-blue-600 px-7 py-3 text-[15px] font-extrabold text-white shadow-[0_14px_28px_rgba(37,99,235,.24)] transition hover:-translate-y-0.5 hover:bg-blue-700 active:translate-y-px">확인</button>
            </div>
          </div>
        </div>
      ) : null}

      {policyModalOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 animate-[modalFade_.20s_ease-out]">
          <div className="w-full max-w-md overflow-hidden rounded-3xl bg-white shadow-2xl animate-[modalPop_.26s_cubic-bezier(.22,1,.36,1)]">
            <div className="flex border-b border-orange-100 bg-orange-50">
              <button type="button" onClick={() => setPolicyTab("terms")} className={`flex-1 py-3 text-center text-sm font-bold ${policyTab === "terms" ? "text-slate-900" : "text-slate-400"}`}>서비스 이용약관</button>
              <button type="button" onClick={() => setPolicyTab("privacy")} className={`flex-1 py-3 text-center text-sm font-bold ${policyTab === "privacy" ? "text-slate-900" : "text-slate-400"}`}>개인정보 처리방침</button>
            </div>
            <div className="max-h-[60vh] overflow-y-auto px-5 py-4 text-xs leading-relaxed text-slate-800 sm:text-sm">
              {policyTab === "terms" ? (
                <>
                  <h2 className="mb-2 text-sm font-bold">제1조 (목적)</h2>
                  <p className="mb-3">이 약관은 주류페이가 제공하는 결제 대행 서비스의 이용과 관련하여 회사와 회원 간의 권리, 의무 및 책임사항을 규정함을 목적으로 합니다.</p>
                  <h2 className="mb-2 text-sm font-bold">제2조 (정의)</h2>
                  <p>서비스란 회원이 회사가 제공하는 결제 대행 서비스를 의미하며, 회원은 서비스에 가입하여 회사를 이용하는 개인 또는 법인을 말합니다.</p>
                </>
              ) : (
                <>
                  <h2 className="mb-2 text-sm font-bold">개인정보 수집 및 이용</h2>
                  <p className="mb-3">회사는 서비스 제공을 위해 이름, 이메일, 연락처, 생년월일, 성별, 신분증 이미지, 인증 결과를 수집할 수 있습니다.</p>
                  <p>수집된 정보는 본인 확인, 서비스 제공, 고객 문의 대응 및 법령 준수 목적에 한해 이용됩니다.</p>
                </>
              )}
            </div>
            <div className="flex justify-end border-t border-orange-100 px-5 py-4"><button type="button" onClick={() => setPolicyModalOpen(false)} className="rounded-2xl bg-slate-900 px-5 py-2.5 text-sm font-bold text-white">닫기</button></div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
